import {
  Box,
  Button,
  FormControl,
  FormLabel,
  HStack,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Text,
  Checkbox,
  VStack,
} from '@chakra-ui/react';
import { AddIcon } from '@chakra-ui/icons';
import { useToastr } from '@laxmimanogna/code-quick-components';
import React, { useContext, useEffect, useState, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import AppColors from '../../../../constants/AppColors';
import { FONT_FAMILY } from '../../../../constants/Theme';
import { AccountContext } from '../../../../providers/AccountsProvider';
import FormMultiSelectModal from '../../../../components/form/FormMultiSelect'

// Add Qualification Modal Component
const AddQualificationModal = ({ isOpen, onClose, onQualificationAdded }) => {
  const toast = useToastr();
  const accountsContext = useContext(AccountContext);

  const qualificationSchema = yup.object().shape({
    qualification: yup
      .string()
      .required('Qualification code is required')
      .max(10, 'Maximum 10 characters'),
    name: yup.string().required('Qualification name is required').max(100),
    category: yup.string().required('Category is required').max(20),
  });

  const {
    handleSubmit,
    reset,
    register,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(qualificationSchema),
  });

  const handleAddQualification = async (formData) => {
    try {
      const response = await accountsContext.createQualification(formData);

      if (response) {
        toast.showSuccess({
          description: 'Qualification added successfully',
        });
        onQualificationAdded(response);
        reset();
        onClose();
      }
    } catch (error) {
      toast.showError({ description: error.message });
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size="lg">
      <ModalOverlay />
      <ModalContent mx={4} px={6}>
        <ModalHeader fontFamily={FONT_FAMILY.baiJamurjee} textAlign="center">
          Add New Qualification
        </ModalHeader>
        <ModalCloseButton />
        <form onSubmit={(e) => {
          e.stopPropagation();
          handleSubmit(handleAddQualification)(e);
        }}>
          <ModalBody pb={6}>
            <FormControl isInvalid={errors.qualification} mb={4}>
              <FormLabel fontSize="xs" fontWeight="bold" color="black">
                Qualification Code
              </FormLabel>
              <Input
                {...register('qualification')}
                placeholder="e.g., MD, NP"
                size="md"
              />
              {errors.qualification && (
                <Text color="red.500" fontSize="xs" mt={1}>
                  {errors.qualification.message}
                </Text>
              )}
            </FormControl>

            <FormControl isInvalid={errors.name} mb={4}>
              <FormLabel fontSize="xs" fontWeight="bold" color="black">
                Qualification Name
              </FormLabel>
              <Input
                {...register('name')}
                placeholder="e.g., Doctor of Medicine"
                size="md"
              />
              {errors.name && (
                <Text color="red.500" fontSize="xs" mt={1}>
                  {errors.name.message}
                </Text>
              )}
            </FormControl>

            <FormControl isInvalid={errors.category}>
              <FormLabel fontSize="xs" fontWeight="bold" color="black">
                Category
              </FormLabel>
              <Select
                {...register('category')}
                placeholder="Select category"
                size="md"
              >
                <option value="DOC">DOC</option>
                <option value="APP">APP</option>
                <option value="OTHER">OTHER</option>
              </Select>
              {errors.category && (
                <Text color="red.500" fontSize="xs" mt={1}>
                  {errors.category.message}
                </Text>
              )}
            </FormControl>
          </ModalBody>

          <ModalFooter justifyContent="center">
            <Button
              type="button"
              mr={3}
              borderRadius="full"
              px={12}
              borderWidth={2}
              borderColor={AppColors.secondary}
              onClick={onClose}
              variant="outline"
            >
              <Text fontWeight="normal" fontSize="sm">
                Cancel
              </Text>
            </Button>
            <Button
              bgColor={AppColors.primary}
              borderRadius="full"
              px={12}
              type="submit"
              isLoading={accountsContext.isQualificationCreating}
            >
              <Text color="white" fontSize="sm">
                Add
              </Text>
            </Button>
          </ModalFooter>
        </form>
      </ModalContent>
    </Modal>
  );
};

// Qualification Selection Modal
const QualificationSelectionModal = ({
  isOpen,
  onClose,
  onSubmit,
  selectedValues,
  options,
  onNewOptionAdded,
}) => {
  const [tempSelectedValues, setTempSelectedValues] = useState(selectedValues || []);
  const [isAddQualModalOpen, setIsAddQualModalOpen] = useState(false);
  const [localOptions, setLocalOptions] = useState(options);
  const listRef = useRef(null);

  useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  }, [localOptions]);

  useEffect(() => {
    setTempSelectedValues(selectedValues || []);
  }, [selectedValues]);

  useEffect(() => {
    setLocalOptions(options);
  }, [options]);

  const handleCheckboxChange = (value) => {
    setTempSelectedValues((prev) => {
      if (prev.includes(value)) {
        return prev.filter((v) => v !== value);
      } else {
        return [...prev, value];
      }
    });
  };

  const handleSubmit = () => {
    onSubmit(tempSelectedValues);
    onClose();
  };

  const handleQualificationAdded = (newQualification) => {
    const newOption = {
      value: newQualification.id,
      label: `${newQualification.qualification} - ${newQualification.name}`,
    };
    
    // Add to local options
    setLocalOptions((prev) => [...prev, newOption]);
    
    // Auto-select the new qualification
    setTempSelectedValues((prev) => [...prev, newQualification.id]);

    // Notify parent so its options list also includes the new one
    onNewOptionAdded?.(newOption);
  };

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} isCentered size="md">
        <ModalOverlay />
        <ModalContent mx={4}>
          <ModalHeader fontFamily={FONT_FAMILY.baiJamurjee} textAlign="center">
            Select Qualifications
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <VStack ref={listRef} align="stretch" spacing={3} maxH="400px" overflowY="auto">
              {localOptions.map((option) => (
                <Checkbox
                  key={option.value}
                  isChecked={tempSelectedValues.includes(option.value)}
                  onChange={() => handleCheckboxChange(option.value)}
                >
                  {option.label}
                </Checkbox>
              ))}
            </VStack>

            <Box
              mt={4}
              p={3}
              borderWidth={1}
              borderColor="gray.200"
              borderRadius="md"
              cursor="pointer"
              _hover={{ bg: 'gray.50' }}
              onClick={(e) => {
                e.stopPropagation();
                setIsAddQualModalOpen(true);
              }}
            >
              <HStack justify="center">
                <AddIcon boxSize={3} color={AppColors.primary} />
                <Text fontSize="sm" fontWeight="medium" color={AppColors.primary}>
                  Add New Qualification
                </Text>
              </HStack>
            </Box>
          </ModalBody>

          <ModalFooter justifyContent="center">
            <Button
              type="button"
              mr={3}
              borderRadius="full"
              px={12}
              borderWidth={2}
              borderColor={AppColors.secondary}
              onClick={onClose}
              variant="outline"
            >
              <Text fontWeight="normal" fontSize="sm">
                Cancel
              </Text>
            </Button>
            <Button
              type="button"
              bgColor={AppColors.primary}
              borderRadius="full"
              px={12}
              onClick={handleSubmit}
            >
              <Text color="white" fontSize="sm">
                Done
              </Text>
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Add Qualification Modal */}
      <AddQualificationModal
        isOpen={isAddQualModalOpen}
        onClose={() => setIsAddQualModalOpen(false)}
        onQualificationAdded={handleQualificationAdded}
      />
    </>
  );
};

// Main Component
const QualificationMultiSelect = ({ control, name = 'qualifications' }) => {
  const [qualifications, setQualifications] = useState([]);
  const accountsContext = useContext(AccountContext);

  const handleNewQualificationAdded = (newOption) => {
    setQualifications(prev => [...prev, newOption]);
  };

  // Fetch qualifications on component mount
  useEffect(() => {
    const fetchQualifications = async () => {
      try {
        const response = await accountsContext.getQualifications();
        const formattedOptions = response.map((q) => ({
          value: q.id,
          label: `${q.qualification} - ${q.name}`,
        }));
        setQualifications(formattedOptions);
      } catch (error) {
        console.error('Error fetching qualifications:', error);
      }
    };
    fetchQualifications();
  }, []);

  return (
    <FormMultiSelectModal
      control={control}
      name={name}
      options={qualifications}
      placeholder="Select qualifications..."
      renderFormModal={(props) => <QualificationSelectionModal {...props} onNewOptionAdded={handleNewQualificationAdded}/>}
    />
  );
};

export default QualificationMultiSelect;